import './FilterDropdown.less';

import React, { FC, useRef, useState } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';
import { activeFiltersCount$, activeResetFilter$, loggedIn$ } from '../subs';
import { useOutsideClick } from '../hooks/useOutsideClick';
import { resetFilter } from '../handlers';
import { SvgIcon } from '../../../icons/SvgIcon';
import {
    BTN_DEFAULT_CLASS,
    CashinToggle,
    FilterByTimeBtn,
    FilterLabel,
    FilterRadarBtn,
    FilterStreamBtn
} from './Filter';
import clsx from 'clsx';
import { createPortal } from 'react-dom';
import { ErrorBoundary } from '../../../common/ErrorBoundary';
import i18n from '../../../common/i18n';
import { openDialog } from '../config';
import { AnimatePresence, motion } from 'framer-motion';
import { isMobileChannel, motionOpacityVariant } from '../../../common/util';
import { TutorialIntro } from './Tutorial';

const FILTER_INFO_LS = 'turboLiveNewFilterMobile';

const FilterDropdown: FC = () => {
    const ref = useRef(null);
    const derefSub = useReframe();
    const activeCount = derefSub(activeFiltersCount$);
    const [visible, setVisible] = useState(false);
    useOutsideClick(ref, () => setVisible(false));
    const isMobileView = isMobileChannel();
    const toggleFilter = () => {
        if (isMobileView) {
            openDialog(
                <div className="fs-18 text-extra-bold text-center px-30">{i18n('nui.turbo.live.filter.dialog')}</div>,
                <FilterDropdownContent />,
                { priority: 1 }
            );
        } else {
            setVisible(!visible);
        }
    };
    const onClose = () => {
        setVisible(false);
        dispatch(resetFilter);
    };
    return (
        <ErrorBoundary description="TurboLive FilterDropdown">
            <div ref={ref} className="p-rel">
                <button
                    className={clsx(
                        'p-rel flex-center br-r-default-light full-height',
                        `${activeCount ? 'filter-dropdown-active' : 'filter-dropdown-bgr'}`,
                        `${isMobileChannel() ? 'filter-dropdown-m' : 'filter-dropdown'}`
                    )}
                    onClick={toggleFilter}
                    data-atid="tlv-filter-dropdown"
                >
                    <FilterImg active={activeCount} />
                    {activeCount > 0 && (
                        <span className="filter-dropdown-badge p-abs c-blue fs-10 bg-white-ng br-radius-circle text-center">
                            {activeCount}
                        </span>
                    )}
                    <span
                        className={`filter-dropdown-abs p-abs ${
                            activeCount ? 'filter-dropdown-active' : 'filter-dropdown-bgr'
                        }`}
                    />
                </button>
                {!isMobileView && activeCount > 0 && (
                    <button
                        title={i18n('nui.turbo.live.filter.reset')}
                        className="filter-reset p-abs filter-hover px-5 py-5 hover-bgr fs-0"
                        onClick={onClose}
                    >
                        <SvgIcon imgId="close" sizeX="8" sizeY="8" className="c-white" />
                    </button>
                )}
                {visible && (
                    <div className="p-abs filter-dropdown-content filter-dropdown-bgr text-left overflow-auto">
                        <div className="p-rel flex justify-center mt-15 mb-10">
                            <div className="fs-18 text-extra-bold text-center px-30">
                                {i18n('nui.turbo.live.filter.dialog')}
                            </div>
                            <div className="pr-20 p-abs filter-dropdown-close flex">
                                <button
                                    className="btn-no-pad filter-hover"
                                    onClick={() => setVisible(false)}
                                    data-atid="tlv-filter-dropdown-close"
                                >
                                    <SvgIcon imgId="close" sizeX="16" sizeY="16" className="c-black-50" />
                                </button>
                            </div>
                        </div>
                        <FilterDropdownContent />
                    </div>
                )}
                {isMobileView && <FilterDropdownInfo />}
            </div>
        </ErrorBoundary>
    );
};

const FilterImg: FC<{ active?: boolean }> = ({ active }) => (
    <SvgIcon imgId="filter" sizeX="20" sizeY="20" className={active ? 'c-white' : 'filter-dropdown-icon'} />
);

const FilterDropdownContent = () => {
    const derefSub = useReframe();
    const loggedIn = derefSub(loggedIn$);
    const activeResetBtn = derefSub(activeResetFilter$);
    const isMobile = isMobileChannel();
    const LABEL_CLASS = 'br-nui-t d-block pt-15 pb-10';
    const BUTTONS_CLASS = 'flex items-center pb-20';
    const reset = () => {
        dispatch(resetFilter);
    };
    return (
        <>
            <div className={clsx({ 'px-20': !isMobileChannel() })}>
                <FilterLabel otherClass={LABEL_CLASS}>{i18n('nui.turbo.live.filter.sort')}</FilterLabel>
                <div className={BUTTONS_CLASS}>
                    <FilterByTimeBtn />
                </div>
                <FilterLabel otherClass={LABEL_CLASS}>{i18n('nui.turbo.live.filter.by')}</FilterLabel>
                <div className={BUTTONS_CLASS}>
                    <FilterStreamBtn />
                    <FilterRadarBtn />
                </div>
                {loggedIn && (
                    <>
                        <FilterLabel otherClass={LABEL_CLASS}>{i18n('nui.turbo.live.filter.show')}</FilterLabel>
                        <div className={BUTTONS_CLASS}>
                            <CashinToggle />
                            <span className="fs-10 c-black-50 pl-20">{i18n('nui.turbo.live.filter.text')}</span>
                        </div>
                    </>
                )}

                <>
                    <FilterLabel otherClass={LABEL_CLASS}>{i18n('nui.turbo.live.filter.dialog.reset')}</FilterLabel>
                    <div className={BUTTONS_CLASS}>
                        <button
                            className={clsx(BTN_DEFAULT_CLASS, 'hover-bgr filter-btn-cancel c-red', {
                                'btn-disabled': !activeResetBtn
                            })}
                            onClick={reset}
                            data-atid="tlv-filter-dropdown-reset"
                        >
                            {i18n('nui.turbo.live.filter.dialog.reset.btn')}
                        </button>
                    </div>
                </>
            </div>
            {!isMobile && <TutorialIntro />}
        </>
    );
};

const FilterDropdownInfoModal = ({ container }) => {
    const [visible, setVisible] = useState(true);
    const disableClose = e => {
        e.stopPropagation();
    };
    const onClose = () => {
        setTimeout(() => {
            setVisible(false);
        }, 0.3);
        localStorage.setItem(FILTER_INFO_LS, 'true');
    };

    return visible ? (
        <>
            {createPortal(
                <AnimatePresence>
                    <motion.div
                        key="backdrop"
                        variants={motionOpacityVariant}
                        initial="initial"
                        animate="enter"
                        exit="exit"
                        onClick={onClose}
                        className="p-fix filter-dropdown-info"
                    />
                </AnimatePresence>,
                container
            )}
            <div className="flex flex-col p-abs filter-dropdown-info-ico">
                <div
                    className={clsx(
                        `${isMobileChannel() ? 'filter-dropdown-m' : 'filter-dropdown'}`,
                        'flex-center filter-dropdown-bgr'
                    )}
                >
                    <FilterImg />
                </div>
                <div
                    onClick={disableClose}
                    className="filter-dropdown-info-modal p-rel bg-white-ng mx-5 mt-15 px-15 pb-20 br-radius-xs"
                    data-atid="tlv-filter-dropdown-info"
                >
                    <div className="flex justify-end pt-15">
                        <button className="btn-no-pad filter-hover" onClick={onClose}>
                            <SvgIcon imgId="close" sizeX="12" sizeY="12" className="c-black-50" />
                        </button>
                    </div>
                    <h3 className="text-extra-bold c-black-100 fs-14">{i18n('nui.turbo.live.filter.modal')}</h3>
                    <p className="c-black-50 pt-5">{i18n('nui.turbo.live.filter.modal.text')}</p>
                </div>
            </div>
        </>
    ) : null;
};

//TODO: tento komponent sa bude zobrazovat len cca 2 mesiace. Potom sa moze zmazat + prislusne css
const FilterDropdownInfo = () => {
    const container = document.getElementById('ReactRoot');
    const showInfoModal = !localStorage.getItem(FILTER_INFO_LS) && container;
    return showInfoModal ? <FilterDropdownInfoModal container={container} /> : null;
};

export default FilterDropdown;
